import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
    FormBuilder,
    FormGroup,
    FormArray,
    Validators,
    ReactiveFormsModule
} from '@angular/forms';
import {TaskStatus} from '@/common/Enum/TaskStatus.Enum';
import {TaskService} from '@services/task.service';
import {ToastrService} from 'ngx-toastr';
import {FarmEnvironmentService} from '@services/farm-environment.service';
import {PlacementService} from '@services/placement.service';
import {FarmUserServiceService} from '@services/farm-user-service.service';
import {futureDateValidator} from '@/common/validator/futureDateValidator';

@Component({
    selector: 'app-add-task',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule],
    templateUrl: './add-task.component.html',
    styleUrl: './add-task.component.scss'
})
export class AddTaskComponent implements OnInit {
    @Input() visible: boolean = false;
    @Input() mode: 'create' | 'edit' | 'view' = 'create';
    @Input() initialTask: any | null = null;
    @Output() closeDialog = new EventEmitter<void>();
    @Output() taskSaved = new EventEmitter<any>();

    taskForm: FormGroup;
    minDate: Date;

    constructor(
        private fb: FormBuilder,
        private taskService: TaskService,
        private toastr: ToastrService,
        private farmEnvService: FarmEnvironmentService,
        private placementService: PlacementService,
        private farmUserService: FarmUserServiceService
    ) {
        this.minDate = new Date();
        this.initializeForm();
    }

    userDetail: any;

    ngOnInit(): void {
        const userDetailString = localStorage.getItem('UserDetail');
        this.userDetail = userDetailString
            ? JSON.parse(userDetailString)
            : null;
        const userId = this.userDetail?.id;
        if (userId) {
            this.loadFarms(userId);
        }
        this.loadFarmUsers();
        this.loadTaskCategories();
        this.loadTaskStatuses();

        // Prefill if editing or viewing
        if (
            (this.mode === 'edit' || this.mode === 'view') &&
            this.initialTask
        ) {
            this.applyInitialTaskPatch(this.initialTask);
        }
        // Disable the form in view mode
        if (this.mode === 'view') {
            this.taskForm.disable({emitEvent: false});
        }
    }

    private initializeForm() {
        this.taskForm = this.fb.group({
            title: ['', [Validators.required]],
            assignee: ['', [Validators.required]],
            startDate: ['', [Validators.required]],
            dueDate: ['', [Validators.required, futureDateValidator()]],
            category: ['', [Validators.required]],
            status: [1], // default to Not Started (id: 1)
            farm: ['', [Validators.required]],
            house: [''],
            partition: [''],
            cycle: [''],
            attachments: [[]],
            subTasks: this.fb.array([])
        });
    }

    get subTasksFormArray(): FormArray {
        return this.taskForm.get('subTasks') as FormArray;
    }

    users: Array<{id: number; name: string; email?: string; phone?: string}> =
        [];

    categories: Array<{id: number; name: string}> = [];
    statusOptions: Array<{id: number; name: string}> = [];

    farms: Array<{id: number; name: string}> = [];
    houses: Array<{id: number; name: string}> = [];
    partitions: Array<{id: number; name: string}> = [];
    cycles: Array<{id: number; name: string}> = [];

    onClose() {
        this.closeDialog.emit();
        this.resetForm();
    }

    async onSave() {
        if (!this.taskForm.valid) {
            this.markFormGroupTouched(this.taskForm);
            return;
        }

        try {
            const form = this.taskForm.value as any;
            if (this.mode === 'edit') {
                const updated = {
                    id: this.initialTask?.id,
                    title: form.title,
                    assigneeId: this.toNumberOrNull(form.assignee),
                    assignee: this.users.find(
                        (u) => u.id === this.toNumberOrNull(form.assignee)
                    )?.name,
                    startDate: this.toIsoString(form.startDate),
                    dueDate: this.toIsoString(form.dueDate),
                    categoryId: this.toNumberOrNull(form.category),
                    categoryName: this.categories.find(
                        (c) => c.id === this.toNumberOrNull(form.category)
                    )?.name,
                    statusId: this.toNumberOrNull(form.status) ?? 1,
                    statusName: this.statusOptions.find(
                        (s) => s.id === (this.toNumberOrNull(form.status) ?? 1)
                    )?.name,
                    farmId: this.toNumberOrNull(form.farm),
                    farm: this.farms.find(
                        (f) => f.id === this.toNumberOrNull(form.farm)
                    )?.name,
                    houseId: this.toNumberOrNull(form.house),
                    houseName: this.houses.find(
                        (h) => h.id === this.toNumberOrNull(form.house)
                    )?.name,
                    partitionId: this.toNumberOrNull(form.partition),
                    partitionName: this.partitions.find(
                        (p) => p.id === this.toNumberOrNull(form.partition)
                    )?.name,
                    cycleId: this.toNumberOrNull(form.cycle),
                    cycle: this.cycles.find(
                        (c) => c.id === this.toNumberOrNull(form.cycle)
                    )?.name
                };
                this.taskSaved.emit(updated);
                this.onClose();
                return;
            }

            const mainAttachment = await this.getFirstFileBase64(
                form.attachments
            );
            const subTasksPayload = await Promise.all(
                this.subTasksFormArray.controls.map(async (ctrl) => {
                    const val = ctrl.value;
                    const subAttach = await this.getFirstFileBase64(
                        val.attachments
                    );
                    return {
                        title: val.title,
                        assigneeId: this.toNumberOrNull(val.assignee),
                        startDate: this.toIsoString(val.startDate),
                        dueDate: this.toIsoString(val.dueDate),
                        statusId: this.toNumberOrNull(val.status) ?? 1,
                        subTaskAttachment: subAttach
                    };
                })
            );
            const payload = {
                title: form.title,
                assigneeId: this.toNumberOrNull(form.assignee),
                farmId: this.toNumberOrNull(form.farm),
                farmHouseId: this.toNumberOrNull(form.house),
                partitionId: this.toNumberOrNull(form.partition),
                cycleId: this.toNumberOrNull(form.cycle),
                startDate: this.toIsoString(form.startDate),
                dueDate: this.toIsoString(form.dueDate),
                categoryId: this.toNumberOrNull(form.category),
                statusId: this.toNumberOrNull(form.status) ?? 1,
                attachment: mainAttachment,
                subTasks: subTasksPayload
            };

            this.taskService.createTask(payload).subscribe({
                next: (res) => {
                    const first = Array.isArray(res) ? res[0] : res;
                    const msg = first?.message || 'Task created successfully';
                    this.toastr.success(msg);
                    this.taskSaved.emit(first);
                    this.onClose();
                }
            });
        } catch (e) {
            this.toastr.error('Failed to create task. Please try again.');
        }
    }

    addSubTask() {
        const subTaskGroup = this.fb.group({
            id: [''],
            title: ['', [Validators.required]],
            assignee: ['', [Validators.required]],
            dueDate: ['', [Validators.required]],
            startDate: ['', [Validators.required]],
            status: [TaskStatus.NotStarted],
            attachments: [[]]
        });

        this.subTasksFormArray.push(subTaskGroup);
    }

    removeSubTask(index: number) {
        this.subTasksFormArray.removeAt(index);
    }

    onFileSelected(
        event: any,
        isSubTask: boolean = false,
        subTaskIndex?: number
    ) {
        const files = Array.from(event.target.files) as File[];
        if (isSubTask && subTaskIndex !== undefined) {
            const subTaskGroup = this.subTasksFormArray.at(
                subTaskIndex
            ) as FormGroup;
            subTaskGroup.patchValue({attachments: files});
        } else {
            this.taskForm.patchValue({attachments: files});
        }
    }

    isFormValid(): boolean {
        return this.taskForm.valid;
    }

    private markFormGroupTouched(formGroup: FormGroup) {
        Object.keys(formGroup.controls).forEach((field) => {
            const control = formGroup.get(field);
            control?.markAsTouched({onlySelf: true});

            if (control instanceof FormGroup) {
                this.markFormGroupTouched(control);
            } else if (control instanceof FormArray) {
                control.controls.forEach((arrayControl) => {
                    if (arrayControl instanceof FormGroup) {
                        this.markFormGroupTouched(arrayControl);
                    }
                });
            }
        });
    }

    private resetForm() {
        this.taskForm.reset();
        this.initializeForm();
    }

    private toNumberOrNull(v: any): number | null {
        if (v === null || v === undefined || v === '') return null;
        const n = typeof v === 'number' ? v : parseInt(v, 10);
        return isNaN(n) ? null : n;
    }

    private toIsoString(dateInput: any): string | null {
        if (!dateInput) return null;
        const d = new Date(dateInput);
        return isNaN(d.getTime()) ? null : d.toISOString();
    }

    private async getFirstFileBase64(
        files: File[] | undefined
    ): Promise<string | null> {
        if (!files || files.length === 0) return null;
        const b64 = await this.fileToBase64(files[0]);
        // strip data URL prefix if present
        const commaIdx = b64.indexOf(',');
        return commaIdx > -1 ? b64.substring(commaIdx + 1) : b64;
    }

    private fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (e) => reject(e);
            reader.readAsDataURL(file);
        });
    }
    private loadFarms(userId: number) {
        this.farmEnvService.getdashboardFarmList(userId).subscribe((result) => {
            const list = Array.isArray(result)
                ? result
                : result?.farms || result || [];
            this.farms = list.map((f: any) => ({id: f.id, name: f.name}));
        });
    }

    private loadHouses(farmId: number) {
        if (!farmId) {
            this.houses = [];
            return;
        }
        this.farmEnvService
            .getdashboardFarmHouseList(farmId)
            .subscribe((result) => {
                const list = Array.isArray(result)
                    ? result
                    : result?.farmHouses || result?.houses || result || [];
                this.houses = list.map((h: any) => ({id: h.id, name: h.name}));
            });
    }

    private loadPartitions(houseId: number) {
        if (!houseId) {
            this.partitions = [];
            return;
        }
        this.farmEnvService
            .getdashboardFarmHousePartitionList(houseId)
            .subscribe((result) => {
                const list = Array.isArray(result)
                    ? result
                    : result?.farmHousePartitions ||
                      result?.partitions ||
                      result ||
                      [];
                this.partitions = list.map((p: any) => ({
                    id: p.id,
                    name: p.name
                }));
            });
    }

    private loadTaskCategories() {
        this.taskService.getTaskCategories().subscribe({
            next: (result) => {
                const list = Array.isArray(result) ? result : [];
                this.categories = list.map((c: any) => ({
                    id: c.id,
                    name: c.name
                }));
            },
            error: () => {
                this.categories = [];
            }
        });
    }
    private loadTaskStatuses() {
        this.taskService.getTaskStatus().subscribe({
            next: (result) => {
                const list = Array.isArray(result) ? result : [];
                this.statusOptions = list.map((c: any) => ({
                    id: c.id,
                    name: c.name
                }));
            },
            error: () => {
                this.statusOptions = [];
            }
        });
    }

    onFarmSelected(value: any) {
        const farmId = this.toNumberOrNull(value);
        this.taskForm.patchValue({house: '', partition: '', cycle: ''});
        this.houses = [];
        this.partitions = [];
        this.cycles = [];
        if (farmId) this.loadHouses(farmId);
        this.loadCycles();
    }

    onHouseSelected(value: any) {
        const houseId = this.toNumberOrNull(value);
        this.taskForm.patchValue({partition: '', cycle: ''});
        this.partitions = [];
        this.cycles = [];
        if (houseId) this.loadPartitions(houseId);
        this.loadCycles();
    }

    onPartitionSelected(value: any) {
        this.taskForm.patchValue({cycle: ''});
        this.loadCycles();
    }

    private loadCycles() {
        const userId = this.userDetail?.id || 0;
        const farmId =
            this.toNumberOrNull(this.taskForm.get('farm')?.value) || 0;
        const houseId =
            this.toNumberOrNull(this.taskForm.get('house')?.value) || 0;
        const partitionId =
            this.toNumberOrNull(this.taskForm.get('partition')?.value) || 0;
        const filterParams = {
            userId,
            flockNo: '',
            placmentDate: null,
            farmId,
            farmHouseId: houseId,
            page: 0,
            pageSize: 0,
            partitionId
        };
        this.placementService.getPlacementList(filterParams).subscribe({
            next: (result) => {
                const placements = Array.isArray(result)
                    ? result
                    : result?.placements || result || [];
                this.cycles = placements.map((p: any) => ({
                    id: p.id,
                    name: p.flockNo
                }));
            },
            error: () => {
                this.cycles = [];
            }
        });
    }

    private loadFarmUsers(
        name: string = '',
        page: number = 1,
        pageSize: number = 50
    ) {
        const currentUserId = this.userDetail?.id;
        if (!currentUserId) {
            this.users = [];
            return;
        }
        this.farmUserService
            .getFarmUserList(currentUserId, name, page, pageSize)
            .subscribe({
                next: (result) => {
                    const list = Array.isArray(result?.farmUsers)
                        ? result.farmUsers
                        : result?.data || result?.items || [];
                    this.users = list.map((u: any) => ({
                        id: u.id,
                        name:
                            u.name ||
                            [u.firstName, u.lastName].filter(Boolean).join(' ')
                    }));
                },
                error: () => {
                    this.users = [];
                }
            });
    }

    private applyInitialTaskPatch(task: any) {
        const toNum = (v: any) =>
            v === undefined || v === null || v === '' ? null : Number(v);
        const patch: any = {
            title: task.title ?? '',
            assignee: toNum(task.assigneeId) ?? null,
            startDate: task.startDate ?? '',
            dueDate: task.dueDate ?? '',
            category: toNum(task.categoryId) ?? null,
            status: toNum(task.statusId) ?? 1,
            farm: toNum(task.farmId) ?? null,
            house: toNum(task.houseId) ?? null,
            partition: toNum(task.partitionId) ?? null,
            cycle: toNum(task.cycleId) ?? null
        };
        this.taskForm.patchValue(patch);
        if (patch.farm) {
            this.loadHouses(patch.farm);
        }
        if (patch.house) {
            this.loadPartitions(patch.house);
        }
        this.loadCycles();
    }
}
